import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { device } from '../../styles/responsive';

// This would be your actual resume file path
const RESUME_PATH = '/MQZ_Resume_2025.pdf';

const Icon = styled.span`
  color: white;
  opacity: 0.8;
  transition: all 0.3s;
  font-size: 14px;
  
  @media ${device.sm} {
    font-size: 12px;
  }
`;

const DownloadContainer = styled(motion.div)`
  position: absolute;
  right: 20px;
  bottom: 20px;
  padding: 12px 24px;
  background: rgba(7, 205, 205, 0.05);
  border: 1px solid rgba(7, 205, 205, 0.2);
  border-radius: 30px;
  cursor: pointer;
  overflow: hidden;
  z-index: 10;
  
  @media ${device.md} {
    display: none; /* Hide on mobile - moved to hamburger menu */
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at center,
      rgba(7, 205, 205, 0.1) 0%,
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(7, 205, 205, 0.4);
    box-shadow: 0 5px 15px rgba(7, 205, 205, 0.1);
    transform: translateY(-3px);

    &::before {
      opacity: 1;
    }

    ${Icon} {
      opacity: 1;
      color: #07CDCD;
      transform: scale(1.1);
    }
  }
`;

const DownloadText = styled.span`
  color: white;
  font-size: 14px;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media ${device.md} {
    font-size: 13px;
    gap: 6px;
  }
  
  @media ${device.sm} {
    font-size: 12px;
    gap: 5px;
  }
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #07CDCD;
  z-index: 1;
  border-radius: 30px;
  
  @media ${device.sm} {
    border-radius: 20px;
  }
`;

const ResumeDownload = () => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    
    setIsDownloading(true);
    
    // Simulate download progress
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Actual download logic
    const link = document.createElement('a');
    link.href = RESUME_PATH;
    link.download = 'MQZ_Resume_2025.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Reset after download
    setTimeout(() => {
      setIsDownloading(false);
    }, 500);
  };

  return (
    <DownloadContainer
      onClick={handleDownload}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 1.2 }}
    >
      <AnimatePresence>
        {isDownloading && (
          <ProgressBar
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
          />
        )}
      </AnimatePresence>
      <DownloadText>
        <Icon>
          <FontAwesomeIcon icon={faDownload} />
        </Icon>
        {isDownloading ? 'Downloading...' : t('Download my Resume')}
      </DownloadText>
    </DownloadContainer>
  );
};

export default ResumeDownload; 